import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Paper from '@mui/material/Paper/Paper';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';

import Order from '@/shared/types/order/order.type';
import ProductOrder from '@/shared/types/productOrder.type';
import formatCurrency from '@/shared/utils/formatCurrency';

type ReceiptProps = {
  products: ProductOrder[];
  currency?: Order['currency'];
};

const Receipt: FC<ReceiptProps> = ({ products, currency }) => {
  const { t } = useTranslation(['checkout', 'common']);

  const getPriceValue = (price: ProductOrder['price']) => {
    if (Array.isArray(price)) {
      return price.find((item) => item.currency_code === currency)?.value;
    }

    return price;
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant='body1' mb={2}>
        {t('checkout:receipt')}
      </Typography>

      <Stack spacing={2}>
        {products.map((product) => (
          <Stack
            key={product?.id}
            direction='row'
            justifyContent='space-between'
          >
            <Stack>
              <Typography color='primary'>
                {product.product_name}
                {product.variant_name && `: ${product.variant_name}`}
              </Typography>
              <Typography color='text.secondary'>
                {product.variant_id
                  ? `${t('checkout:variantId')}: ${product.variant_id?.split('-')[0]}`
                  : `${t('checkout:productId')}: ${product.product_id}`}
              </Typography>
            </Stack>

            <Typography variant='body1' sx={{ display: 'flex', gap: 1 }}>
              <span>{product.product_qty}x</span>
              <span>
                {formatCurrency(
                  parseFloat(getPriceValue(product.price) as string),
                )}
              </span>
              <span>
                {formatCurrency(
                  parseFloat(getPriceValue(product.price) as string) *
                    parseInt(product.product_qty),
                )}
              </span>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export default Receipt;
